import React, { useState, useRef } from 'react';
import { Typography } from '@fiverr-private/typography';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Button } from '@fiverr-private/button';
import { ElementType } from '@fiverr-private/bianka_js';
import { useEnteredView } from '@fiverr-private/hooks';
import { translate } from '../../shared/helpers';
import GigPreviewCard from '../GigPreviewCard';
import GigPreviewsLayout from '../GigPreviewsLayout';
import GigCategoriesMenu from '../GigCategoriesMenu';
import FluidSection from '../FluidSection';
import { sendBiankaImpression, sendBiankaClick, sendMixpanelEvent } from '../../shared/biEvents';
import { EVENT_NAMES } from '../../shared/biEvents/eventNames';
import { enrichUrlWithParams } from '../../shared/urlEnrichment';
import {
  GIG_CATEGORIES as GIG_CATEGORIES_DEFAULT,
  GIG_CATEGORIES_V16,
  GIG_PREVIEWS,
  GIG_CARDS_MAX_LIMIT,
} from './constants';
import { headingCustomClass, buttonClass, sectionClass } from './styles.ve.css';

const PopularServices: React.FC<{ isV16?: boolean }> = ({ isV16 = false }) => {
  const GIG_CATEGORIES = isV16 ? GIG_CATEGORIES_V16 : GIG_CATEGORIES_DEFAULT;
  const [selectedGigCategoryIndex, setSelectedGigCategoryIndex] = useState(0);
  const observableRef = useRef<HTMLDivElement>(null);

  useEnteredView(observableRef, {
    threshold: 1,
    onEntered: () => {
      sendBiankaImpression({
        eventName: EVENT_NAMES.BIANKA.CATALOG_SERVICES_IS_SHOWN,
        componentName: 'popular_services_catalog',
        componentText: translate('popular_services.title'),
        isV16,
      });
    },
  });

  const handleGigCategoryClick = (index: number) => {
    sendBiankaClick({
      eventName: EVENT_NAMES.BIANKA.CLICKED_ON_CATALOG_SERVICE_BUTTON,
      elementName: 'catalog_service_button',
      elementType: ElementType.TAB,
      elementText: GIG_CATEGORIES[index].name,
      position: index + 1,
      isV16,
    });

    sendMixpanelEvent({
      eventName: EVENT_NAMES.MIXPANEL.LOHP_CLICKED_CATALOG_SERVICE_BUTTON,
      clickedElement: GIG_CATEGORIES[index].name,
      clickedElementOrder: index + 1,
      isV16,
    });

    setSelectedGigCategoryIndex(index);
  };

  const handleCtaClick = () => {
    sendBiankaClick({
      eventName: EVENT_NAMES.BIANKA.CLICKED_ON_CATALOG_SERVICE_CTA,
      elementName: 'catalog_service_cta',
      elementType: ElementType.BUTTON,
      elementText: GIG_CATEGORIES[selectedGigCategoryIndex].name,
      position: selectedGigCategoryIndex + 1,
      isV16,
    });

    sendMixpanelEvent({
      eventName: EVENT_NAMES.MIXPANEL.LOHP_CLICKED_CATALOG_CTA,
      elementSource: GIG_CATEGORIES[selectedGigCategoryIndex].name,
      elementSourceOrder: selectedGigCategoryIndex + 1,
      isV16,
    });
  };

  const selectedCategoryName = GIG_CATEGORIES[selectedGigCategoryIndex]?.name;
  const filteredGigs = GIG_PREVIEWS.filter((gig) => gig.categoryName === selectedCategoryName).slice(
    0,
    GIG_CARDS_MAX_LIMIT
  );

  const selectedCategoryLink = enrichUrlWithParams(
    GIG_CATEGORIES[selectedGigCategoryIndex]?.link || '',
    'hplo_catalog'
  );

  return (
    <FluidSection
      paddingTop={{
        default: '8',
        sm: '16',
        md: '20',
        xl: '24',
      }}
      paddingBottom={{
        default: '8',
        sm: '16',
        md: '12',
        xl: '20',
      }}
      className={sectionClass}
    >
      <Typography as="h2" color="grey_1200" className={headingCustomClass} ref={observableRef}>
        {translate('popular_services.title')}
      </Typography>

      <GigCategoriesMenu
        categories={GIG_CATEGORIES}
        onCategorySelect={handleGigCategoryClick}
        selectedIndex={selectedGigCategoryIndex}
      />

      <Container maxWidth="100%" width="100%" data-testid="gig-previews-container">
        <GigPreviewsLayout>
          {filteredGigs.map((gig, index) => (
            <GigPreviewCard
              key={index}
              coverImage={gig.coverImage}
              userName={gig.userName}
              rating={gig.rating}
              reviewsCount={gig.reviewsCount}
              showProBadge={gig.showProBadge}
              showTopRatedBadge={gig.showTopRatedBadge}
              showFiverrChoiceBadge={gig.showFiverrChoiceBadge}
              showSellerLevelTwoBadge={gig.showSellerLevelTwoBadge}
              gigLink={gig.gigLink}
              sellerLink={gig.sellerLink}
              avatarImage={gig.avatarImage}
              categoryName={gig.categoryName}
              gigDescription={gig.gigDescription}
              position={index}
              isV16={isV16}
            />
          ))}
        </GigPreviewsLayout>
      </Container>

      <Stack
        justifyContent="center"
        alignItems="center"
        paddingRight={{
          default: '5',
          sm: '0',
        }}
      >
        <Button
          href={selectedCategoryLink}
          intent="primary"
          variant="filled"
          size="lg"
          disabled={false}
          loading={false}
          fullWidth={{
            default: true,
            sm: false,
          }}
          className={buttonClass}
          onClick={handleCtaClick}
        >
          {translate('popular_services.view_more_experts')}
        </Button>
      </Stack>
    </FluidSection>
  );
};

export default PopularServices;
